/**
 * Centralized Organization Configuration
 *
 * This file contains all organization-specific settings for white-label partners.
 * Adding a new organization should only require changes to this file.
 */

import { datadogLogs } from '@datadog/browser-logs';
import LogoGreen from '@public/berry-street-logo-green.svg';
import LogoLockupGreen from '@public/berry-street-logo-lockup-green.svg';
import LogoLockupWhite from '@public/berry-street-logo-lockup-white.svg';
import LogoWhite from '@public/berry-street-logo-white.svg';
import LogoGreenNS from '@public/nutrisense/ns_logo_full_green.svg';
import LogoWhiteNS from '@public/nutrisense/ns_logo_full_white.svg';

// Organization IDs - single source of truth
export const ORG_IDS = {
  BERRY_STREET: 2884,
  BERRY_STREET_STAGING: 15958,
  NUTRISENSE_PROD: 9077,
  NUTRISENSE_STAGING: 35153,
  HEALTHIFY_PROD: 98525,
  HEALTHIFY_STAGING: 196333,
} as const;

// Environment helper
const isProduction = () => process.env.NEXT_PUBLIC_APP_ENV === 'production';

const getCurrentBerryStreetOrgId = () =>
  isProduction() ? ORG_IDS.BERRY_STREET : ORG_IDS.BERRY_STREET_STAGING;

// Current Nutrisense org ID based on environment
const getCurrentNutrisenseOrgId = () =>
  isProduction() ? ORG_IDS.NUTRISENSE_PROD : ORG_IDS.NUTRISENSE_STAGING;

const getCurrentHealthifyOrgId = () =>
  isProduction() ? ORG_IDS.HEALTHIFY_PROD : ORG_IDS.HEALTHIFY_STAGING;

// Legacy compatibility - computed at runtime
export const WhiteLabelOrgs = {
  get NUTRISENSE() {
    return getCurrentNutrisenseOrgId();
  },
  get BERRY_STREET() {
    return getCurrentBerryStreetOrgId();
  },
  get HEALTHIFY() {
    return getCurrentHealthifyOrgId();
  },
} as const;

export type WhiteLabelOrgs =
  (typeof WhiteLabelOrgs)[keyof typeof WhiteLabelOrgs];

/**
 * All whitelabel organization IDs — every configured org EXCEPT Berry Street,
 * which is our main org. Includes both prod and staging IDs so the check is
 * correct in either environment. Used to decide whether a provider should be
 * offered ONLY their org's templates (the whitelabel "swap") rather than the
 * global Berry Street set.
 */
export const WHITELABEL_ORG_IDS: ReadonlySet<number> = new Set(
  Object.values(ORG_IDS).filter(
    (id) => id !== ORG_IDS.BERRY_STREET && id !== ORG_IDS.BERRY_STREET_STAGING,
  ),
);

/** True when the org is a whitelabel partner (i.e. not Berry Street / unknown). */
export const isWhitelabelOrg = (orgId?: number | null): boolean =>
  orgId != null && WHITELABEL_ORG_IDS.has(orgId);

// Logo configuration - purely data-driven approach
export interface LogoConfig {
  /** Alt text for logo */
  altText: string;
  /** CSS filter for brands that reuse a monochrome asset as their white logo. */
  whiteLogoFilter?: string;
  /** Logo variants by color and size */
  variants: {
    /** Dark/default color logos */
    dark: {
      /** Standard size logo */
      standard: string;
      /** Large/lockup size logo */
      large: string;
    };
    /** Light/white color logos */
    light: {
      /** Standard size logo */
      standard: string;
      /** Large/lockup size logo */
      large: string;
    };
  };
  /** Helper method to get logo src - no sizing logic */
  getLogoSrc: (params: {
    logoColor?: 'dark' | 'white';
    variant?: 'default' | 'light';
  }) => string;
}

// Branding configuration
export interface BrandingConfig {
  /** Company name for display */
  companyName: string;
  /** Primary domain */
  domain: string;
  /** Meeting/origin domain */
  origin?: string;
  /** Logo configuration */
  logos: LogoConfig;
  /** Favicon configuration */
  favicon: {
    /** Path to favicon */
    path: string;
    /** Path to apple touch icon */
    appleTouchPath: string;
  };
}

// Application-specific configuration
export interface AppConfig {
  /** Should show background illustrations */
  showBackgroundIllustrations: boolean;
  /** Redirect URL for patient app */
  patientRedirectUrl?: string;
  /** Provider sign-in redirect URL */
  signInRedirectUrl?: string;
}

// Complete organization configuration
export interface OrganizationConfig {
  /** Organization ID */
  id: number;
  /** Branding configuration */
  branding: BrandingConfig;
  /** Application configuration */
  app: AppConfig;
}

// Berry Street configuration for either environment
const createBerryStreetConfig = (orgId: number): OrganizationConfig => ({
  id: orgId,
  branding: {
    companyName: 'Berry Street',
    domain: 'berrystreet.co',
    logos: {
      altText: 'Berry Street Logo',
      variants: {
        dark: {
          standard: LogoGreen,
          large: LogoLockupGreen,
        },
        light: {
          standard: LogoWhite,
          large: LogoLockupWhite,
        },
      },
      getLogoSrc: ({ logoColor }) => {
        const colorKey = logoColor === 'white' ? 'light' : 'dark';
        const variants = {
          dark: {
            standard: LogoGreen,
            large: LogoLockupGreen,
          },
          light: {
            standard: LogoWhite,
            large: LogoLockupWhite,
          },
        };
        // Use lockup (large) version for Berry Street header/company logo
        return variants[colorKey].large;
      },
    },
    favicon: {
      path:
        process.env.NEXT_PUBLIC_APP_ENV === 'production'
          ? 'favicon.ico'
          : 'favicon-staging.ico',
      appleTouchPath:
        process.env.NEXT_PUBLIC_APP_ENV === 'production'
          ? '/favicon-apple-touch.png.ico'
          : '/favicon-staging-apple-touch.png.ico',
    },
  },
  app: {
    showBackgroundIllustrations: true,
    patientRedirectUrl: process.env.NEXT_PUBLIC_PATIENT_REDIRECT_URL,
    signInRedirectUrl: `https://meet${isProduction() ? '' : '-staging'}.berrystreet.co/sign-in`,
  },
});

// Create Nutrisense config based on org ID
const createNutrisenseConfig = (orgId: number): OrganizationConfig => {
  const isProd = orgId === ORG_IDS.NUTRISENSE_PROD;

  return {
    id: orgId,
    branding: {
      companyName: 'Nutrisense',
      domain: 'nutrisense.io',
      origin: isProd ? 'meet.nutrisense.io' : 'meet-staging.nutrisense.io',
      logos: {
        altText: 'Nutrisense Logo',
        variants: {
          dark: {
            standard: LogoGreenNS,
            large: LogoGreenNS, // Same logo for both sizes
          },
          light: {
            standard: LogoWhiteNS,
            large: LogoWhiteNS, // Same logo for both sizes
          },
        },
        getLogoSrc: ({ logoColor, variant }) => {
          // Nutrisense prioritizes variant over logoColor for backward compatibility
          const colorKey =
            variant === 'light'
              ? 'light'
              : logoColor === 'white'
                ? 'light'
                : 'dark';
          const variants = {
            dark: {
              standard: LogoGreenNS,
              large: LogoGreenNS, // Same logo for both sizes
            },
            light: {
              standard: LogoWhiteNS,
              large: LogoWhiteNS, // Same logo for both sizes
            },
          };
          return variants[colorKey].standard;
        },
      },
      favicon: {
        path: '/nutrisense/ns_favicon.png',
        appleTouchPath: '/nutrisense/ns_favicon.png',
      },
    },
    app: {
      showBackgroundIllustrations: false,
      patientRedirectUrl: 'https://members.nutrisense.io/video-calls/sign-in',
      // Must use berrystreet domain for provider login to avoid satellite domain issues
      signInRedirectUrl: `https://meet${isProd ? '' : '-staging'}.berrystreet.co/sign-in?org=${orgId}`,
    },
  };
};

// Create Healthify config based on org ID
const createHealthifyConfig = (orgId: number): OrganizationConfig => {
  const isProd = orgId === ORG_IDS.HEALTHIFY_PROD;

  return {
    id: orgId,
    branding: {
      companyName: 'Healthify',
      domain: 'healthifyme.com',
      origin: isProd ? 'meet.healthifyme.com' : 'meet-staging.healthifyme.com',
      logos: {
        altText: 'Healthify Logo',
        whiteLogoFilter: 'brightness(0) invert(1)',
        variants: {
          dark: {
            standard: '/healthify/healthify_logo_horizontal_black.png',
            large: '/healthify/healthify_logo_horizontal_black.png', // Same logo for all variants
          },
          light: {
            standard: '/healthify/healthify_logo_horizontal_black.png',
            large: '/healthify/healthify_logo_horizontal_black.png', // Same logo for all variants
          },
        },
        getLogoSrc: () => '/healthify/healthify_logo_horizontal_black.png', // Always returns same logo
      },
      favicon: {
        path: '/healthify/healthify_favicon.ico',
        appleTouchPath: '/healthify/healthify_favicon.ico',
      },
    },
    app: {
      showBackgroundIllustrations: false,
      patientRedirectUrl: 'https://www.healthifyme.com/us/',
      // Must use berrystreet domain for provider login to avoid satellite domain issues
      signInRedirectUrl: `https://meet${isProd ? '' : '-staging'}.berrystreet.co/sign-in?org=${orgId}`,
    },
  };
};

// Centralized configuration registry
export const ORGANIZATION_CONFIGS: Record<number, OrganizationConfig> = {
  [ORG_IDS.BERRY_STREET]: createBerryStreetConfig(ORG_IDS.BERRY_STREET),
  [ORG_IDS.BERRY_STREET_STAGING]: createBerryStreetConfig(
    ORG_IDS.BERRY_STREET_STAGING,
  ),
  [ORG_IDS.NUTRISENSE_PROD]: createNutrisenseConfig(ORG_IDS.NUTRISENSE_PROD),
  [ORG_IDS.NUTRISENSE_STAGING]: createNutrisenseConfig(
    ORG_IDS.NUTRISENSE_STAGING,
  ),
  [ORG_IDS.HEALTHIFY_PROD]: createHealthifyConfig(ORG_IDS.HEALTHIFY_PROD),
  [ORG_IDS.HEALTHIFY_STAGING]: createHealthifyConfig(ORG_IDS.HEALTHIFY_STAGING),
};

// Main API - get organization configuration
export const getOrganizationConfig = (orgId?: number): OrganizationConfig => {
  // Log when using fallback config
  if (!orgId) {
    datadogLogs.logger.warn(
      '[getOrganizationConfig] Called with undefined orgId, falling back to Berry Street config. ' +
        'This may indicate a race condition where organization data has not loaded yet. ' +
        'Consider using useOrganizationConfig() which includes loading state.',
    );
  }

  return (
    (orgId && ORGANIZATION_CONFIGS[orgId]) ||
    ORGANIZATION_CONFIGS[getCurrentBerryStreetOrgId()]
  );
};

// Get org ID by origin/domain lookup
export const getOrganizationIdByOrigin = (origin: string): number => {
  const config = Object.values(ORGANIZATION_CONFIGS).find(
    (config) =>
      config.branding.origin === origin || config.branding.domain === origin,
  );
  if (config?.branding.domain === 'berrystreet.co') {
    return getCurrentBerryStreetOrgId();
  }
  return config?.id ?? getCurrentBerryStreetOrgId();
};

// Utility functions
export const getDomainFromWindow = (): string => {
  const parts = window.location.origin.split('.');
  return parts.slice(-2).join('.');
};

export const getWelcomeCompanyName = (orgId?: number): string => {
  return getOrganizationConfig(orgId).branding.companyName;
};

// Legacy compatibility exports
export const whiteLabelDomains = Array.from(
  new Set(
    Object.values(ORGANIZATION_CONFIGS)
      .map((config) => config.branding.domain)
      .filter((domain) => domain !== 'berrystreet.co'),
  ),
);

export const WhiteLabelsOrigins = Object.fromEntries(
  Object.values(ORGANIZATION_CONFIGS)
    .filter((config) => config.branding.origin)
    .map((config) => [config.id, config.branding.origin as string]),
);

export const WhiteLabelsDomain = Object.fromEntries(
  Object.values(ORGANIZATION_CONFIGS).map((config) => [
    config.id,
    config.branding.domain,
  ]),
);
